/* Analýza V2+ — datový model a sady polí.
 *
 * Odděleno od AnalyzaV2Page.js schválně: stránka táhne recharts, framer-motion
 * i axios, takže by se tvar dat nedal otestovat bez mountování celého UI
 * (a axios jako ESM v jestu padá). Tady je to čistá data + čisté funkce.
 */

import { prepocet, normMena, zakladniMena, VYCHOZI_MENA } from "@/lib/analyzaV2/meny";
/* Příjmy/výdaje pro fázi „Příjmy a výdaje“ — týž výpočet jako formulář a plán. */
import { volneCashflow } from "@/lib/bilance";
/* Souhlasy klienta (Ano / Později / Ne) — tvar a normalizace ze sdíleného
 * prodejního kitu, ať analýza a Analýza pro Vietnamce ukládají totéž. */
import { normalizujSouhlas } from "@/lib/prodej/souhlas";
/* Souhlas s pořadím cílů („Schod po schodu“, Č12) — snímek, jen se čte. */
import { normalizujSouhlasPoradi } from "@/lib/analyzaV2/souhlasPoradi";
/* Forma příjmu se ukládá jako jedna hodnota bez ohledu na jazyk poradce. */
import { normFormaPrijmu } from "@/lib/analyzaV2/prezentace";
/* Limit pojistky „zapsaný / prázdný“ — týž výklad jako srovnání pojištění
 * a výstraha „Koho pojišťuje?“ (výslovná nula = údaj). */
import { asCislo } from "@/lib/bezpecneHodnoty";
/* Rizika pojistky po osobách (`rizikaOsob`, 14. 10. 2026): výslovná nula
 * uvnitř je údaj stejně jako u plochých limitů. */
import { rizikaOsobMajiUdaj } from "@/lib/analyzaV2/kohoPojistuje";
/* Předvyplněné cíle šablony (c1–c4) — modul bez importů, ať je čte i stavy.js
 * a dopocetCile.js bez kruhu přes model.js (13. 10. 2026). */
import { cileSablony } from "@/lib/analyzaV2/sablonaCilu";
/* Fáze „Cíle“ hotová i s cílem, jehož částku plán dopočítá (CX2-10, 13. 10. 2026). */
import { dopocetCile } from "@/lib/analyzaV2/dopocetCile";

/* ===================================================================
   SOUHLASY KLIENTA V ANALÝZE (29. 9. 2026)

   `souhlas.gdpr`      — souhlas se zpracováním osobních údajů pro analýzu.
                         Zaškrtnutí: `{ kdy: ISO }`, nezaškrtnuto = `null`.
                         ⚠️ OD 5. 10. 2026 SE NESBÍRÁ (majitel: „k ničemu“;
                         analýza a rada stojí na čl. 6 odst. 1 b) a c) GDPR,
                         ne na souhlasu). Zaškrtávátko je pryč z formuláře
                         i z prezentace, ✓ Představení nese „Ano, má čas“
                         (prezentaceChovani.js) a tisk ho neukazuje.
                         NEMAZAT: zápisy ze starších analýz se dál načítají
                         a ukládají beze změny (normalizujGdpr níž).
   `souhlas.rizika`    — „Dává vám smysl řešit zajištění příjmu?“
                         ⚠️ OD 5. 10. 2026 SE NEPTÁ (majitel: „tohle neni
                         potreba“) — otázka je pryč z formuláře i z prezentace,
                         krok Rizika splní Kino rizik (`rizikaProbrana` níž,
                         uložená odpověď ✓ nenese). NEMAZAT: uložené odpovědi
                         se dál načítají a ukládají; čte je jen starý plán
                         (plneniCilu.ochranaOdmitnuta) — analýza (Co spolu
                         řešíme, Schod po schodu) je nečte.
   `souhlas.dalsiKrok` — „Chcete v tom pokračovat na další schůzce?“
                         ⚠️ OD 5. 10. 2026 SE NEPTÁ (majitel: „to dej pryc“)
                         — otázka je pryč z formuláře i z prezentace. ✓ Závěru
                         nese termín další schůzky (prezentaceChovani.js),
                         tisk odpověď neukazuje (zaverTisk.js).
                         NEMAZAT: uložené odpovědi se dál načítají a ukládají
                         beze změny, ale nic je nečte.
                         Obojí `{ odpoved: "ano"|"pozdeji"|"ne", kdy, poznamka }`
                         z lib/prodej/souhlas.js, nebo `null`.

   NEZADÁNO JE `null`, NE „ne“. Nezodpovězená otázka není odmítnutí —
   stejně jako prázdné číslo není nula. Oba režimy (formulář i prezentace)
   píšou do TÉHOŽ místa, takže co klient řekl v prezentaci, poradce vidí
   i ve formuláři a naopak.
=================================================================== */

/** Uložený GDPR souhlas → `{ kdy }` nebo `null`. Neplatné datum se
 *  nevymýšlí: souhlas platí, jen `kdy` zůstane `null`. */
export function normalizujGdpr(x) {
  if (!x || typeof x !== "object") return null;
  const d = x.kdy ? new Date(x.kdy) : null;
  return { kdy: d && !Number.isNaN(d.getTime()) ? d.toISOString() : null };
}

/** Všechny tři souhlasy v bezpečném tvaru (chybějící = `null`).
 *  `poradi` (souhlas s pořadím cílů ze „Schod po schodu“, Č12) jen když je
 *  zapsaný — dřív ho tahle funkce při načtení tiše zahodila. Bez zápisu má
 *  objekt tvar jako dřív (prázdná analýza se nemění). */
export function normalizujSouhlasy(src) {
  const s = src && typeof src === "object" ? src : {};
  const poradi = normalizujSouhlasPoradi(s.poradi);
  return {
    gdpr: normalizujGdpr(s.gdpr),
    rizika: normalizujSouhlas(s.rizika),
    dalsiKrok: normalizujSouhlas(s.dalsiKrok),
    ...(poradi ? { poradi } : {}),
  };
}

/* Doplní do NAČTENÝCH řádků klíče, které starší analýzy nemají (nová datumová
 * pole V2+). Bez toho by React přepínal input z neřízeného na řízený a hodnota
 * by se při prvním zápisu ztratila.
 *
 * Doplňuje POUZE chybějící klíče — uložená data se nikdy nepřepisují. */
function fillRow(row, defaults) {
  const out = { ...defaults, ...(row || {}) };
  for (const k of Object.keys(defaults)) {
    if (out[k] === undefined || out[k] === null) out[k] = defaults[k];
  }
  return out;
}

const ROW_DEFAULTS = {
  zavazkyB: { instituce: "", urok: 0, splatka: 0, jistina: 0, fixace: "", konec: "", pozn: "" },
  zavazkyO: { instituce: "", urok: 0, splatka: 0, jistina: 0, vyroci: "", pozn: "" },
  /* LIMITY STÁVAJÍCÍ POJISTKY — `limitSmrt` a `limitInvalidita`.
   *
   * ⚠️ NEMAZAT jako „nepoužité“. Jsou to částky, na které je klient pojištěný
   * DNES, a tvoří LEVOU STRANU srovnání v plánu: „původní limit 8 mil. /
   * nový limit 31 mil., platil 6 400 / bude platit 8 200“. Bez nich umí
   * prezentace ukázat jen cenu — a z návrhu se tím stane zdražení.
   * Sbírá se právě smrt a invalidita — tyhle dva limity majitel u klienta
   * srovnává. PN a TNÚ se do srovnání záměrně netahají, aby zůstalo čitelné.
   *
   * VÝCHOZÍ HODNOTA JE PRÁZDNÝ TEXT, NE NULA. Uložené analýzy tyhle klíče
   * nemají a `fillRow` je dopisuje při každém načtení — nula by o klientovi
   * tvrdila „není krytý vůbec“, což je jiná informace než „nevíme“. */
  /* ⚠️ `platba: 0` TU ZŮSTÁVÁ SCHVÁLNĚ, a není to údaj od klienta.
   * Nula drží sčítání a stavy po celé appce (`souhrny.js` → `mesicne`,
   * `stavy.js` → `stavProduktu`, `propsaniSmluv.js`, `doporuceniZAnalyzy.js`);
   * všude jde přes `num()`, kterému "" a 0 vycházejí stejně. V analýze se nula
   * kreslí jako prázdno (`PeniznyInput`: `+v > 0 ? … : ""`).
   * NEZNÁMO OD NULY ROZLIŠUJE TEPRVE PLÁN: `platbaSmlouvy`
   * v lib/srovnaniPojisteni.js bere 0 jako „nevíme", protože z téhle nuly by
   * tabulka „PŘED A PO" jinak vytiskla „platí dnes 0 Kč" — tvrzení, že klient
   * za pojistku neplatí nic (nález revize 24. 9. 2026). Kdyby se tahle nula
   * měnila na "", musí se přepočítat všechna čtyři místa výš. */
  /* KOHO SMLOUVA POJIŠŤUJE — `osoba` („k1" / „k2", prázdno = nevybráno).
   *
   * ⚠️ NEMAZAT a NEPŘEDVYPLŇOVAT. Bez téhle informace nejde srovnání v plánu
   * rozepsat po osobách („invalidita muže / invalidita ženy") — a vymyslet si,
   * čí je stará smlouva, nesmíme. Když je v analýze jen jeden člověk, žádná
   * dvojznačnost není a pole se vyplňovat nemusí; u dvou lidí bez vybrání
   * sečte plán celou domácnost a řekne poradci, ať to vybere.
   * Ukládá se KLÍČ osoby, ne jméno: jméno se v analýze ještě mění.
   * Viz lib/srovnaniPojisteni.js. */
  /* LIMITY ZBYLÝCH RIZIK OSOBY (5. 10. 2026, majitel ke snímku Před a po:
   * „vlevo doplnit rizika předtím, co měl“) — závažná onemocnění, pracovní
   * neschopnost (denní dávka), denní odškodné, trvalé následky. Jen v bublině ⋯
   * (řádek pojistky je na šířku plný). Prázdné = nezadáno, ne nula; tabulka
   * Před a po pak řekne „nezadáno“. */
  pojistky: { instituce: "", cislo: "", platba: 0, limitSmrt: "", limitInvalidita: "", limitZavazne: "", limitPn: "", limitOdskodne: "", limitTnu: "", osoba: "", pocatek: "", vyroci: "", pozn: "", dodaPozdeji: false },
};
/* Cizoměnová položka: korunová `hodnota` je jen ODVOZENÁ, nikdy zdroj pravdy.
 *
 * NÁLEZ KONTROLY: přepočet se dřív dělal POUZE při editaci. Po načtení tedy
 * v datech zůstala stará koruna z doby, kdy kurz ještě platil — UI hlásilo
 * „bez platného kurzu nezapočítáváme", ale souhrn majetku tu položku sečetl.
 * Dvě různé pravdy na jedné obrazovce.
 *
 * Přepočítáváme proto při každém načtení: bez platného kurzu je hodnota 0
 * a položka se do majetku opravdu nezapočítá — tak, jak to UI slibuje. */
function prepoctiRadek(r, base) {
  const zakl = zakladniMena(base);
  const mena = normMena(r?.currency);
  // Domácí měna se počítá PŘÍMO — žádný kurz. Základní měna se bere ze země
  // organizace poradce (nebo z ručního nastavení analýzy), NIKDY z jazyka UI.
  // Dřív tu bylo natvrdo CZK, takže slovenský poradce zadal eura a v souhrnu
  // majetku viděl 0 — euro bylo „cizí měna" bez kurzu.
  if (!mena || mena === zakl) return r;
  const p = prepocet(r, new Date(), zakl);
  const vBase = p.uplne ? Math.round(p.czk) : 0;
  return { ...r, hodnota: vBase, converted_base: p.uplne ? vBase : null };
}

const MAJETEK_DEFAULTS = { instituce: "", hodnota: 0, vklad: 0, zalozeno: "", splatnost: "", pozn: "" };

/* STOPA PO CHYBĚ ŘAZENÍ CÍLŮ (do 29. 9. 2026).
 * Stránka volala `set("cile", …)` místo `set({ cile: … })`. `set` spojuje
 * objekty spreadem, takže `{ ...d, ..."cile" }` do analýzy zapsalo klíče
 * "0"–"3" s písmeny c, i, l, e — a cíle se neseřadily vůbec. Tyhle čtyři
 * klíče nikdo jiný nezapisuje; smažou se jen v PŘESNĚ téhle podobě. */
const STOPA_CILE = { 0: "c", 1: "i", 2: "l", 3: "e" };
function bezStopyRazeni(src) {
  if (!Object.keys(STOPA_CILE).every((k) => src[k] === STOPA_CILE[k])) return src;
  const out = { ...src };
  for (const k of Object.keys(STOPA_CILE)) delete out[k];
  return out;
}

/* PŘEDVYPLNĚNÉ ŘÁDKY, KTERÉ ŠABLONA UŽ NEZAKLÁDÁ (Č4, 4. 10. 2026).
 *
 * Majitel: „rizikové pojištění stačí, úrazový není potřeba, aby tam bylo“.
 * Nová analýza proto řádek „Úrazové pojištění“ (pj2) nemá; druh „Úrazové
 * pojištění“ jde dál vybrat u řádku, který poradce přidá sám.
 *
 * Vzor tu ZŮSTÁVÁ pro STARŠÍ ANALÝZY, které pj2 uložený mají:
 *  · prázdný nedotčený pj2 (nic vyplněného, název ani druh nezměněný) se při
 *    normalizaci vypustí — viz `jePrazdnaOdebranaSablona`;
 *  · pj2 s JAKOUKOLI hodnotou (platba, pojišťovna, číslo, limity, osoba,
 *    data, poznámka, „doda později“, „ruší se / zůstává“) zůstává beze změny
 *    a dál dostane `druh` (A2.13), stav „nevyplněno“ (stavy.js) i překlad
 *    názvu (sablonoveNazvy.js).
 * ⚠️ id „pj2“ dál čte lib/srovnaniPojisteni.js (`POJISTKY_RUSIT_VYCHOZI`) —
 * vyplněný úrazový řádek starší analýzy se tak počítá jako dřív. */
export const ODEBRANE_SABLONY = Object.freeze({
  pojistky: Object.freeze([
    Object.freeze({ id: "pj2", oznaceni: "Úrazové pojištění", druh: "uraz", instituce: "", cislo: "", platba: 0, limitSmrt: "", limitInvalidita: "", limitZavazne: "", limitPn: "", limitOdskodne: "", limitTnu: "", osoba: "", pocatek: "", vyroci: "", pozn: "", dodaPozdeji: false }),
  ]),
});

/* Je hodnota „nic“? Prázdný text (i jen mezery), 0, false, null, prázdné pole
 * či objekt. Text „0“ ani jiné číslo nicotou NENÍ — to už někdo zapsal. */
function jeNic(v) {
  if (v === undefined || v === null || v === false || v === 0) return true;
  if (typeof v === "string") return v.trim() === "";
  if (Array.isArray(v)) return v.every(jeNic);
  if (typeof v === "object") return Object.values(v).every(jeNic);
  return false;
}
const stejnyText = (a, b) => String(a ?? "").trim().toLowerCase() === String(b ?? "").trim().toLowerCase();

/* LIMITY SMRTI A INVALIDITY: výslovná NULA je zapsaný údaj, ne prázdno.
 * Srovnání pojištění (`maLimit` v lib/srovnaniPojisteni.js) i výstraha
 * „Koho pojišťuje?“ (`chybiKohoPojistuje` v ZavazkyTabulka.js) čtou limit přes
 * `asCislo` a 0 berou jako limit — řádek s limitem 0 tedy mění „Před a po“
 * a vypustit se nesmí. Prázdný je limit jen tehdy, když ho nebere ani jeden
 * z nich (`""`, null, undefined; mezery `asCislo` čte jako 0 → taky údaj). */
const KLICE_LIMITU = ["limitSmrt", "limitInvalidita", "limitZavazne", "limitPn", "limitOdskodne", "limitTnu"];
const limitNic = (v) => jeNic(v) && asCislo(v) == null;
/* Hodnota klíče pojistky je „nic“? Limity a `rizikaOsob` podle pravidla
 * limitů (výslovná nula = údaj), ostatní podle `jeNic`. */
const hodnotaNic = (k, v) => (k === "rizikaOsob" ? !rizikaOsobMajiUdaj(v)
  : KLICE_LIMITU.includes(k) ? limitNic(v) : jeNic(v));

/* PŘÍJMY: „Co dělá za práci" (`prace1` / `prace2`, Č7 4. 10. 2026) je volný
 * text. Starší analýza klíče nemá → prázdný řetězec; cokoli jiného než text
 * (null, číslo, objekt z rozbitého zápisu) → taky prázdno, ať pole nespadne.
 * Částky se tu NEMĚNÍ — jen se doplní dva textové klíče. */
function normPrijmy2(empty, src) {
  const p = { ...(empty || {}), ...(src && typeof src === "object" && !Array.isArray(src) ? src : {}) };
  for (const k of ["prace1", "prace2"]) if (typeof p[k] !== "string") p[k] = "";
  return p;
}

/** Je řádek nedotčený předvyplněný řádek, který šablona už nezakládá?
 *  Shoda id i názvu, druh chybí / prázdný / šablonový a VŠECHNO ostatní je
 *  prázdné. Cokoli zadaného (i jen osoba, „ruší se“ nebo limit 0) = řádek
 *  zůstává. */
export function jePrazdnaOdebranaSablona(key, r) {
  if (!r || typeof r !== "object" || Array.isArray(r)) return false;
  const vzor = (ODEBRANE_SABLONY[key] || []).find((x) => x.id === r.id);
  if (!vzor || !stejnyText(r.oznaceni, vzor.oznaceni)) return false;
  if (!jeNic(r.druh) && r.druh !== vzor.druh) return false;
  return Object.keys(r).every((k) =>
    k === "id" || k === "oznaceni" || k === "druh" || hodnotaNic(k, r[k]));
}

/** Načtená v2 data → tvar, se kterým UI bezpečně pracuje. Nic nemaže
 * (kromě stopy po chybě řazení cílů, viz `bezStopyRazeni`, a prázdných
 * nedotčených řádků, které šablona už nezakládá, viz `ODEBRANE_SABLONY`). */
export function normalizeV2(v2, empty = EMPTY, base) {
  const src = bezStopyRazeni(v2 || {});
  // Základní měna analýzy: uložená v datech (ruční nastavení) > předaná
  // z organizace poradce > výchozí. Ukládá se, aby stará analýza po změně
  // země organizace nezměnila zpětně čísla, která už poradce klientovi ukázal.
  const zaklMena = zakladniMena(src.base_currency || base || VYCHOZI_MENA);
  const majetek = { ...empty.majetek, ...(src.majetek || {}) };
  for (const kat of Object.keys(majetek)) {
    majetek[kat] = (majetek[kat] || []).map((r) => prepoctiRadek(fillRow(r, MAJETEK_DEFAULTS), zaklMena));
  }
  /* A2.13: STARŠÍ ANALÝZY dostanou `druh` u nepřejmenovaných předvyplněných
   * řádků (zb1…, zo1…, pj1…) — JEN když klíč `druh` vůbec nemají. Prázdný
   * text je volba poradce („—“) a ta se nepřepisuje. */
  const druhSablony = (key, r) => {
    if (!r || typeof r !== "object" || Object.prototype.hasOwnProperty.call(r, "druh")) return r;
    const vzory = [...(empty[key] || []), ...(ODEBRANE_SABLONY[key] || [])];
    const vzor = vzory.find((x) => x.id === r.id && x.oznaceni === r.oznaceni && x.druh);
    return vzor ? { ...r, druh: vzor.druh } : r;
  };
  const list = (key) =>
    (src[key]?.length ? src[key] : empty[key])
      .filter((r) => !jePrazdnaOdebranaSablona(key, r))
      .map((r) => fillRow(druhSablony(key, r), ROW_DEFAULTS[key]));
  return {
    ...empty,
    ...src,
    // Zapsat zpátky, ať analýza nese svou základní měnu i po uložení. Bez toho
    // by se po změně země organizace zpětně přepočetla čísla, která poradce
    // klientovi už ukázal a vytiskl.
    base_currency: zaklMena,
    majetek,
    zavazkyB: list("zavazkyB"),
    zavazkyO: list("zavazkyO"),
    pojistky: list("pojistky"),
    prijmy2: normPrijmy2(empty.prijmy2, src.prijmy2),
    vydaje2: { ...empty.vydaje2, ...(src.vydaje2 || {}) },
    rizika: { ...empty.rizika, ...(src.rizika || {}) },
    podklady: { ...empty.podklady, ...(src.podklady || {}) },
    dalsiSchuzka: { ...empty.dalsiSchuzka, ...(src.dalsiSchuzka || {}) },
    souhlas: normalizujSouhlasy(src.souhlas),
    formaPrijmu1: normFormaPrijmu(src.formaPrijmu1),
    formaPrijmu2: normFormaPrijmu(src.formaPrijmu2),
  };
}

export const EMPTY = {
  datum: new Date().toISOString().slice(0, 10),
  klienti: [
    { id: "k1", jmeno: "", prijmeni: "", narozeni: "", telefon: "", email: "" },
    { id: "k2", jmeno: "", prijmeni: "", narozeni: "", telefon: "", email: "" },
  ],
  deti: [],
  /* `casPotvrzen`: klient potvrdil 45 minut (true/false, null = nezadáno).
     Starý text `casovaNarocnost` se nemaže — viz lib/analyzaV2/casSchuzky.js. */
  casovaNarocnost: "", casPotvrzen: null, ocekavani: "", penize: "",
  /* c1 Povinná likvidní rezerva (0 let), c2 Bydlení / rekonstrukce (5),
     c3 Vzdělání pro děti / start do života (15), c4 Finanční nezávislost
     (25, měsíčně) — data v sablonaCilu.js (CILE_SABLONY), tady kopie. */
  cile: cileSablony(),
  cilePoznamka: "",
  majetek: {
    stavebko: [{ id: "s1", oznaceni: "Stavební spoření", instituce: "", hodnota: 0, vklad: 0, urok: 0 }],
    /* `druh` je vyplněný schválně: tyhle tři řádky jsou předvyplněné NÁMI, ne
       poradcem, takže o nich víme jistě, co jsou zač. Rozhoduje podle něj
       našeptávač instituce (DIP smí vést i banka a obchodník s CP, DPS ze
       zákona jen penzijní společnost) i státní příspěvek pod tabulkou —
       ten náleží DPS, u DIP by to bylo tvrzení, které neplatí.
       Řádek přidaný poradcem má druh prázdný, dokud ho nevybere. */
    penzijko: [
      { id: "pz1", oznaceni: "Staré penzijní připojištění", druh: "pripojisteni", instituce: "", hodnota: 0, vklad: 0, zam: 0 },
      { id: "pz2", oznaceni: "Doplňkové penzijní spoření", druh: "dps", instituce: "", hodnota: 0, vklad: 0, zam: 0 },
      { id: "pz3", oznaceni: "Dlouhodobý investiční produkt", druh: "dip", instituce: "", hodnota: 0, vklad: 0, zam: 0 },
    ],
    opf: [
      { id: "o1", oznaceni: "OPF/ETF akciové", instituce: "", hodnota: 0, vklad: 0 },
      { id: "o2", oznaceni: "OPF/ETF dluhopisové", instituce: "", hodnota: 0, vklad: 0 },
      { id: "o3", oznaceni: "OPF/ETF peněžní trh", instituce: "", hodnota: 0, vklad: 0 },
      { id: "o4", oznaceni: "OPF/ETF realitní a jiné", instituce: "", hodnota: 0, vklad: 0 },
      { id: "o5", oznaceni: "Jednotlivé akcie", instituce: "", hodnota: 0, vklad: 0 },
      { id: "o6", oznaceni: "Korporátní dluhopisy", instituce: "", hodnota: 0, vklad: 0 },
      /* ZLATO TU UŽ NENÍ — patří do nefinančního majetku (`nemovitosti.n6`).
       * Fyzické zlato nemá finanční instituci, nevyplácí nic a neprodá se
       * kliknutím; do sekce „OPF / ETF / akcie" ho řadit znamená míchat ho
       * do investičního portfolia, ze kterého se pak počítá alokace.
       * Starším analýzám řádek `o7` zůstane i s částkou — nic se nemaže. */
      { id: "o8", oznaceni: "Kryptoměny", instituce: "", hodnota: 0, vklad: 0 },
    ],
    kzp: [
      { id: "kz1", oznaceni: "Kapitálové životní pojištění", instituce: "", hodnota: 0, vklad: 0 },
      { id: "kz2", oznaceni: "Investiční životní pojištění", instituce: "", hodnota: 0, vklad: 0 },
    ],
    ucty: [
      { id: "u1", oznaceni: "Termínovaný vklad", instituce: "", hodnota: 0, urok: 0, vklad: 0 },
      { id: "u2", oznaceni: "Spořicí účet", instituce: "", hodnota: 0, urok: 0, vklad: 0 },
      { id: "u3", oznaceni: "Běžný účet", instituce: "", hodnota: 0, urok: 0, vklad: 0 },
      { id: "u4", oznaceni: "Hotovost pod polštářem", instituce: "", hodnota: 0, urok: 0, vklad: 0 },
      { id: "u5", oznaceni: "Cizí měny", instituce: "", hodnota: 0, urok: 0, vklad: 0 },
    ],
    nemovitosti: [
      { id: "n1", oznaceni: "Byt / rodinný dům", prijem: 0, hodnota: 0 },
      { id: "n2", oznaceni: "Komerční prostory", prijem: 0, hodnota: 0 },
      { id: "n3", oznaceni: "Pozemky / lesy / pole", prijem: 0, hodnota: 0 },
      { id: "n4", oznaceni: "Auto / vozidla", prijem: 0, hodnota: 0 },
      { id: "n5", oznaceni: "Cennosti (hodinky, umění, sbírky…)", prijem: 0, hodnota: 0 },
      { id: "n6", oznaceni: "Zlato a drahé kovy", prijem: 0, hodnota: 0 },
    ],
    firma: [{ id: "f1", oznaceni: "Firma / podnikání", prijem: 0, hodnota: 0 }],
    dedictvi: [{ id: "d1", oznaceni: "Dědictví", hodnota: 0 }],
  },
  zavazkyB: [
    /* `druh` předvyplněný (A2.13, 3. 10. 2026) — stejně jako u penzijka: tyhle
       řádky zakládáme MY, víme, co jsou zač, a poradce dřív viděl ve sloupci
       Druh „—“. Hodnoty = SABLONA_DRUH v zavazkyTaby.js, takže se zařazení
       do tabu nemění. Hotovost řádku ani propis do složky `druh` nečtou. */
    { id: "zb1", oznaceni: "Hypoteční úvěr / úvěr na bydlení", druh: "hypoteka", instituce: "", urok: 0, splatka: 0, jistina: 0, fixace: "", konec: "", pozn: "" },
    { id: "zb2", oznaceni: "Spotřebitelské úvěry", druh: "spotrebitelsky", instituce: "", urok: 0, splatka: 0, jistina: 0, fixace: "", konec: "", pozn: "" },
    { id: "zb3", oznaceni: "Leasing", druh: "leasing_osobni", instituce: "", urok: 0, splatka: 0, jistina: 0, fixace: "", konec: "", pozn: "" },
    { id: "zb4", oznaceni: "Kreditky", druh: "kreditni_karta", instituce: "", urok: 0, splatka: 0, jistina: 0, fixace: "", konec: "", pozn: "" },
  ],
  zavazkyO: [
    { id: "zo1", oznaceni: "Podnikatelský úvěr", druh: "podnikatelsky", instituce: "", urok: 0, splatka: 0, jistina: 0, vyroci: "", pozn: "" },
    { id: "zo2", oznaceni: "Revolvingový úvěr", druh: "revolving", instituce: "", urok: 0, splatka: 0, jistina: 0, vyroci: "", pozn: "" },
  ],
  // Pojistné smlouvy, které klient MÁ před analýzou — propíšou se do složky klienta
  // (sekce Smlouvy) a u každé jde vygenerovat výpověď do pojišťovny (§ 2807 NOZ).
  // „Úrazové pojištění“ (pj2) se od 4. 10. 2026 nezakládá — viz ODEBRANE_SABLONY.
  pojistky: [
    { id: "pj1", oznaceni: "Rizikové životní pojištění", druh: "zivot", instituce: "", cislo: "", platba: 0, limitSmrt: "", limitInvalidita: "", limitZavazne: "", limitPn: "", limitOdskodne: "", limitTnu: "", osoba: "", pocatek: "", vyroci: "", pozn: "", dodaPozdeji: false },
    { id: "pj3", oznaceni: "Pojištění nemovitosti / domácnosti", druh: "nemovitost", instituce: "", cislo: "", platba: 0, limitSmrt: "", limitInvalidita: "", limitZavazne: "", limitPn: "", limitOdskodne: "", limitTnu: "", osoba: "", pocatek: "", vyroci: "", pozn: "", dodaPozdeji: false },
    { id: "pj4", oznaceni: "Pojištění odpovědnosti", druh: "odpovednost", instituce: "", cislo: "", platba: 0, limitSmrt: "", limitInvalidita: "", limitZavazne: "", limitPn: "", limitOdskodne: "", limitTnu: "", osoba: "", pocatek: "", vyroci: "", pozn: "", dodaPozdeji: false },
    { id: "pj5", oznaceni: "Povinné ručení / havarijní", druh: "povinne_ruceni", instituce: "", cislo: "", platba: 0, limitSmrt: "", limitInvalidita: "", limitZavazne: "", limitPn: "", limitOdskodne: "", limitTnu: "", osoba: "", pocatek: "", vyroci: "", pozn: "", dodaPozdeji: false },
    { id: "pj6", oznaceni: "Pojištění právní ochrany", druh: "pravni_ochrana", instituce: "", cislo: "", platba: 0, limitSmrt: "", limitInvalidita: "", limitZavazne: "", limitPn: "", limitOdskodne: "", limitTnu: "", osoba: "", pocatek: "", vyroci: "", pozn: "", dodaPozdeji: false },
  ],
  /* PŘÍJMY: šest souhrnných polí je ZDROJ PRAVDY pro solver, plán, radar i tisk.
   * `detail` = nepovinný rozpis položka po položce (lib/analyzaV2/prijmyDetail.js).
   *   Píše se do něj, jen když si ho poradce rozbalí, a součty zapisuje JEDNÍM
   *   krokem do těch šesti polí — dvě nesladěné úrovně by znamenaly jiné číslo
   *   v analýze a jiné v plánu (týž důvod jako u `vydaje2.rozpis`).
   *
   * ⚠️ BUDOUCÍ PŘÍJMY TU NEJSOU. Leží v `pritoky` (pole bez defaultu, protože
   * ho dřív plnil jen Návrh řešení) a zadávají se i v analýze — viz
   * lib/analyzaV2/budouciPrijmy.js. Jsou to peníze, které JEŠTĚ NEJSOU, takže
   * do dnešní bilance nevstupují; plán z nich od daného roku zvedne kapacitu.
   * Nepřelévat je do `prijmy2` — bilance by ukázala příjem, který klient nemá.
   *
   * `prace1` / `prace2` = „Co dělá za práci" (volný text, Č7 4. 10. 2026).
   * Nikam se nesčítá ani nepočítá — poznámka k osobě (components/analyza/PrijmyOsoby.js). */
  prijmy2: { prokaz1: 0, prokaz2: 0, ostatni1: 0, ostatni2: 0, pasivni: 0, bonusyRok: 0, pozn: "", detail: {}, prace1: "", prace2: "" },
  /* `detail` = deset souhrnných kategorií (koláč v prezentaci).
   * `rozpis` = detailní katalog položka po položce (vydajeKatalog.js); píše se
   *   do něj jen když si ho poradce rozbalí, a součty z něj zapisuje JEDNÍM
   *   krokem do `obvykle`/`minimalni` a `detail` — plán totiž čte výdaje jako
   *   max(obvyklé, součet kategorií), takže dvě nesladěné úrovně by znamenaly
   *   jiné číslo v analýze a jiné v plánu.
   * `pravidlo` = PŘIZNÁNÍ, že souhrn vyplnilo pravidlo 50/30/20 z příjmu, ne
   *   klient. `null` = čísla jsou od klienta (nebo se to neví). */
  vydaje2: { obvykle: 0, minimalni: 0, pozn: "", detail: {}, rozpis: {}, pravidlo: null },
  /* MĚSÍČNÍ PŘEBYTEK ODSOUHLASENÝ KLIENTEM — VÝCHOZÍ STAV JE PRÁZDNO, NE NULA.
   *
   * NÁLEZ 25. 9. 2026 (bylo v produkci): tady stálo `prebytek: 0`. Protože
   * `normalizeV2` níž dolévá defaulty a stránka ukládá CELÉ `d`, uložila se
   * nula i do analýzy, kde na to pole poradce vůbec nesáhl. Plán pak tu nulu
   * čte jako ODSOUHLASENOU („klient mi potvrdil, že volné peníze nejsou“),
   * zastaví se a ukáže přebytek 0 Kč a rentu 0 % — přestože analýza o dvě
   * obrazovky výš počítá volné cash flow 21 500 Kč/měs.
   *
   * NULA JE ODPOVĚĎ, PRÁZDNO NENÍ. Je to táž zásada jako u `zadanaCastka`
   * (planSolver), `zadano` (rozpocetPlanu) a `asCislo` (planEngine): `""`
   * → `null` → plán si volné cash flow DOPOČÍTÁ a označí ho za dopočet
   * (`prebytekZdroj: "dopocet"`), zadaná nula zůstává odsouhlasenou nulou.
   *
   * ⚠️ STARÉ ANALÝZY SE TÍM NEMĚNÍ, a to schválně: `normalizeV2` přepisuje
   * default hodnotou z uložených dat, takže analýza s uloženou nulou dál
   * vychází jako odsouhlasená nula. Rozeznat v ní vyplněnou nulu od naší
   * dosazené NEJDE (viz `prebytekNejisty` v planEngine.js) — a přepsat ji
   * naslepo by znamenalo zahodit poradcovo „klient nemá nic volného“.
   *
   * UI (opraveno 29. 9. 2026): pole v analýze dřív ukládalo `+v`, takže po
   * vymazání obsahu spadla do dat nula („klient odsouhlasil nulu“). Teď oba
   * režimy (formulář i prezentace) posílají pro vymazané pole `null` — plán
   * ho přes `asCislo` bere stejně jako `""`: neodsouhlaseno, dopočítat.
   * Vyplněná nula zůstává nulou a na obrazovce je vidět jako „0“. */
  prebytek: "",
  rizika: {
    potrebaResit: "", navrh: "",
    majetkova: [
      { id: "r1", oznaceni: "Pojištění nemovitosti / stavby", soucasna: 0, nova: 0 },
      { id: "r2", oznaceni: "Pojištění domácnosti", soucasna: 0, nova: 0 },
      { id: "r3", oznaceni: "Pojištění odpovědnosti", soucasna: 0, nova: 0 },
      { id: "r4", oznaceni: "Povinné ručení", soucasna: 0, nova: 0 },
      { id: "r5", oznaceni: "Havarijní pojištění", soucasna: 0, nova: 0 },
    ],
  },
  coBude: "",
  // Závěr schůzky: co má klient dodat + kdy se sejdeme příště.
  // Nové v V2+, staré analýzy je nemají → bezpečné defaulty (viz normalizeRows).
  /* „Co má dodat klient“ (5. 10. 2026, lib/analyzaV2/podkladyKlienta.js): seznam se
   * skládá sám z analýzy. Staré klíče (pojistne … vlastni) zůstávají jako obecné
   * volby; nově `danove` (null = podle formy příjmu, OSVČ = ano), `vypnute`
   * (odškrtnuté automatické řádky `zdroj:id`), `vlastniSeznam`, `jazyk` zprávy
   * ("" = jazyk appky) a `odeslano` — jen kdy, kanál, komu (k1/k2), způsob
   * a OTISK zprávy. Text zprávy ani adresa se neukládají. */
  podklady: { pojistne: false, investicni: false, uverove: false, vypisy: false, dotaznik: false, vlastni: "", danove: null, vypnute: [], vlastniSeznam: [], jazyk: "", odeslano: null },
  dalsiSchuzka: { datum: "", cas: "", pozn: "" },
  formaPrijmu1: "", formaPrijmu2: "",
  reference: [],
  referenceGdpr: false,
  referenceGdprDatum: "",
  /* Souhlasy klienta — viz `normalizujSouhlasy` výš. Nezadáno = null. */
  souhlas: { gdpr: null, rizika: null, dalsiKrok: null },
};

/* Sady polí smluv — jediný zdroj pravdy pro prezentaci i testy.
 *
 * POZOR: musí to být FUNKCE beroucí měnu parametrem, ne konstanty. Symbol se
 * v aplikaci plní až za běhu z useLanguage — pole vyhodnocené při importu by
 * navždy zamrzlo na „Kč“ a slovenský poradce by v kartách smluv viděl koruny.
 * Stejná past jako u webových kalkulaček.
 */
export function poleHypo(cur) {
  return [
    { k: "instituce", l: "Instituce", list: "av-instituce" },
    { k: "jistina", l: "Zůstatek jistiny", type: "number", suffix: cur },
    { k: "splatka", l: "Měsíční splátka", type: "number", suffix: cur },
    { k: "urok", l: "Úrok", type: "number", suffix: "%" },
    { k: "fixace", l: "Konec fixace", type: "date" },
    { k: "konec", l: "Konec splácení", type: "date" },
    { k: "pozn", l: "Poznámka", wide: true },
  ];
}
export function poleZavazek(cur) {
  return [
    { k: "instituce", l: "Instituce", list: "av-instituce" },
    { k: "jistina", l: "Zůstatek jistiny", type: "number", suffix: cur },
    { k: "splatka", l: "Měsíční splátka", type: "number", suffix: cur },
    { k: "urok", l: "Úrok", type: "number", suffix: "%" },
    { k: "vyroci", l: "Výročí / splatnost", type: "date" },
    { k: "pozn", l: "Poznámka", wide: true },
  ];
}
export function polePojistka(cur) {
  return [
    { k: "instituce", l: "Instituce", list: "av-instituce" },
    { k: "cislo", l: "Číslo smlouvy" },
    { k: "platba", l: "Měsíční platba", type: "number", suffix: cur },
    /* NA CO JE KLIENT POJIŠTĚNÝ DNES — limity (invalidita, smrt, PN, ZO, TNÚ,
     * denní odškodné) už NEJSOU pole smlouvy: od 14. 10. 2026 jsou po osobách
     * v `rizikaOsob` a zadávají se v tabulce Rizika ve smlouvách
     * (components/analyza/RizikaSmluv, lib/analyzaV2/kohoPojistuje.js).
     * Starý plochý tvar (`limitSmrt`…, `osoba`) se dál čte beze změny čísel —
     * klíče drží ROW_DEFAULTS.pojistky, jen se do nich nepíše. */
    { k: "pocatek", l: "Datum počátku", type: "date" },
    { k: "vyroci", l: "Výročí", type: "date" },
    { k: "pozn", l: "Poznámka", wide: true },
    { k: "dodaPozdeji", l: "Dokument dodá klient později", type: "check" },
  ];
}
export function poleInvestice(cur) {
  return [
    { k: "instituce", l: "Instituce", list: "av-instituce" },
    { k: "hodnota", l: "Současná hodnota", type: "number", suffix: cur },
    { k: "vklad", l: "Pravidelná platba", type: "number", suffix: cur },
    { k: "zalozeno", l: "Datum založení", type: "date" },
    { k: "splatnost", l: "Splatnost / konec", type: "date" },
    { k: "pozn", l: "Poznámka", wide: true },
  ];
}

/* ===================================================================
   FÁZE SCHŮZKY V ZÁHLAVÍ ANALÝZY (audit 29. 9. 2026)

   Záhlaví dřív ukazovalo natvrdo „✓ Úvod ✓ Hodnoty ● Cíle“ — bez ohledu na
   to, co bylo vyplněné. Nováček si pak myslel, že má úvod hotový, a nevěděl,
   kde je. Stav fáze se teď počítá z dat:
     hotovo   = v té části je zapsaný skutečný údaj (✓),
     aktualni = PRVNÍ fáze, kde ještě nic není — kam pokračovat (●),
     ceka     = ostatní.
   Nula ani prázdný předvyplněný řádek údajem nejsou.
=================================================================== */
export const FAZE_SCHUZKY = ["Úvod", "Hodnoty", "Cíle", "Zdroje", "Závazky", "Příjmy a výdaje", "Rizika"];

const kladne = (v) => Number.isFinite(+v) && +v > 0;
const textJe = (v) => typeof v === "string" && v.trim() !== "";
const radky = (v) => (Array.isArray(v) ? v.filter((r) => r && typeof r === "object") : []);

/** Co musí být zapsané, aby fáze byla hotová. Klíč = název fáze. */
const FAZE_HOTOVA = {
  /* Kdo je klient: jméno, příjmení a datum narození hlavní osoby. */
  "Úvod": (d) => {
    const k = radky(d.klienti)[0] || {};
    return textJe(k.jmeno) && textJe(k.prijmeni) && textJe(k.narozeni);
  },
  /* Klientova slova — očekávání od schůzky nebo co pro něj znamenají peníze. */
  "Hodnoty": (d) => textJe(d.ocekavani) || textJe(d.penize),
  /* Aspoň jeden cíl s částkou (stejně jako kompletnost „cíl“) — NEBO cíl,
   * jehož částku plán dopočítá (renta s termínem, rezerva při zadaných
   * nezbytných výdajích; nedotčená šablona ne). Rozhodnutí hlavní relace
   * 13. 10. 2026 (CX2-10): krok Cíle je hotový i s dopočtem renty — tutéž
   * fajfku pak vidí lišta průvodce, čip prezentace (hotoveKroky) i nový CRM,
   * stejně jako povinné minimum Cíl v bráně (dopocetCile.js). */
  "Cíle": (d) => radky(d.cile).some((c) => kladne(c.hodnota) || dopocetCile(d, c) !== null),
  /* Aspoň jedna položka majetku s hodnotou nebo pravidelným vkladem.
   * Nebo vědomý zápis „Bez majetku“ u sekce bez jediného údaje (nemaZapsano). */
  "Zdroje": (d) => {
    const m = d.majetek && typeof d.majetek === "object" ? d.majetek : {};
    return Object.values(m).some((kat) => radky(kat).some((r) => kladne(r.hodnota) || kladne(r.vklad) || kladne(r.zam)))
      || nemaZapsano(d, "majetek");
  },
  /* Úvěr s jistinou/splátkou, nebo stávající pojistka (platba či instituce).
   * Nebo vědomý zápis „Bez závazků a pojistek“ u sekce bez jediného údaje. */
  "Závazky": (d) =>
    [...radky(d.zavazkyB), ...radky(d.zavazkyO)].some((r) => kladne(r.jistina) || kladne(r.splatka))
    || radky(d.pojistky).some((r) => kladne(r.platba) || textJe(r.instituce))
    || nemaZapsano(d, "zavazky"),
  /* Příjmy i výdaje — teprve pak jde spočítat volné cash flow. */
  "Příjmy a výdaje": (d) => volneCashflow(d).znamo,
  /* A2.4 (audit 3. 10. 2026): JEDNO MĚŘÍTKO pro hlavičku, fáze i čipy
   * prezentace — viz rizikaProbrana níž. */
  "Rizika": (d) => rizikaProbrana(d),
};

/* ===================================================================
   „KLIENT NIC NEMÁ“ (průvodce analýzou, nález CX-06, 12. 10. 2026)

   Klient bez majetku a bez dluhů dřív nikdy nedošel na „9 z 9“: krok Majetek
   je hotový jen s položkou, která má hodnotu, a nulu zapsat nejde (správně —
   nula není odpověď). Poradce proto smí v kroku BEZ JEDINÉHO ÚDAJE vědomě
   zapsat „Klient nic nemá“ → `d.pruvodce.nema.{majetek|zavazky} = { kdy }`.

   Platí jen, dokud v sekci opravdu nic není. Jakmile poradce zapíše cokoli
   (i jen instituci, poznámku nebo výslovnou nulu limitu), zápis přestane
   platit sám — „nemá“ a rozepsaný řádek nemůžou být pravda zároveň.
   Nezadáno, „nemá“ a nula zůstávají tři různé věci.

   Čte to stavFazi výš, takže tutéž fajfku vidí lišta průvodce, čipy
   prezentace (hotoveKroky) i nový CRM — jedno měřítko. (Kontrola 2. kola V4:
   hlavní relace 12. 10. 2026 potvrdila, že krok hotový i v prezentaci je
   logický důsledek odsouhlaseného náčrtu — neměnit.)
=================================================================== */

/* Klíče, které řádek popisují, ale nejsou údajem od klienta: název a druh
 * zakládá šablona, měnu nabízí formulář. */
const POPISNE_KLICE = new Set(["id", "oznaceni", "druh", "typ", "currency"]);

/** Má řádek aspoň jeden údaj, který zadal poradce? Limit pojistky
 *  s výslovnou nulou je údaj (viz `limitNic`). */
function radekSUdajem(r) {
  if (!r || typeof r !== "object" || Array.isArray(r)) return false;
  return Object.entries(r).some(([k, v]) => !POPISNE_KLICE.has(k) && !hodnotaNic(k, v));
}

/** Klíče sekcí, u kterých jde zapsat „Klient nic nemá“. */
export const SEKCE_NEMA = Object.freeze(["majetek", "zavazky"]);

/**
 * Je sekce bez jediného údaje? Předvyplněné řádky šablony (jen název)
 * údajem nejsou.
 * @param {object} d analýza (v2)
 * @param {"majetek"|"zavazky"} klic
 */
export function sekceBezUdaju(d, klic) {
  const data = d && typeof d === "object" ? d : {};
  if (klic === "majetek") {
    const m = data.majetek && typeof data.majetek === "object" && !Array.isArray(data.majetek) ? data.majetek : {};
    return !Object.values(m).some((kat) => radky(kat).some(radekSUdajem));
  }
  if (klic === "zavazky") {
    return ![...radky(data.zavazkyB), ...radky(data.zavazkyO), ...radky(data.pojistky)].some(radekSUdajem);
  }
  return false;
}

/**
 * Platí zápis „Klient nic nemá“? Jen když je zapsaný A sekce je pořád bez údaje.
 * @param {object} d analýza (v2)
 * @param {"majetek"|"zavazky"} klic
 */
export function nemaZapsano(d, klic) {
  const data = d && typeof d === "object" ? d : {};
  const p = data.pruvodce && typeof data.pruvodce === "object" ? data.pruvodce : {};
  const nema = p.nema && typeof p.nema === "object" ? p.nema : {};
  const z = nema[klic];
  if (!z || typeof z !== "object" || Array.isArray(z)) return false;
  return sekceBezUdaju(data, klic);
}

/**
 * Záznam „poradce pustil Kino rizik“ do `d.rizikaProbrana` (5. 10. 2026).
 * Čas je čas spuštění kina. Zapisuje se jen jednou (první spuštění).
 * @returns {{ kdy: string }}
 */
export function zaznamProbraniRizik(ted = new Date()) {
  const cas = ted instanceof Date && !Number.isNaN(ted.getTime()) ? ted : new Date();
  return { kdy: cas.toISOString() };
}

/**
 * KROK RIZIKA JE SPLNĚNÝ, KDYŽ HO PORADCE PROŠEL KINEM (5. 10. 2026).
 *
 * Otázku „Dává vám smysl řešit zajištění příjmu?“ majitel z kroku odebral
 * („tohle neni potreba aby to tam bylo“) — v kroku zbyl skript a Kino rizik,
 * nic se nevyplňuje. Splněno = poradce pustil Kino rizik (ve formuláři nebo
 * v prezentaci) → `d.rizikaProbrana = { kdy }` (zaznamProbraniRizik). Je to
 * jediná akce, kterou krok má, a jde z dat poznat. Pohyb mezi kroky prezentace
 * se schválně NEPOČÍTÁ: šipka „Další“ neříká, že se o rizicích mluvilo.
 *
 * STARŠÍ ANALÝZY: zapsaná odpověď na zrušenou otázku (`souhlas.rizika`,
 * i „Ano“) zůstává v datech, ale ✓ UŽ NENESE (nález ověření 5. 10. 2026) —
 * poradce ji nikde nevidí, a ✓ nesmí svítit kvůli něčemu, co nevidí. Totéž
 * měřítko jako u odebraného GDPR a „Chcete pokračovat…?“
 * (prezentaceChovani.js `hotoveKroky`). Stačí znovu pustit Kino rizik.
 *
 * JEDNO MĚŘÍTKO (audit 3. 10. 2026, A2.4): hlavička „X z 9 oblastí“, fáze
 * schůzky i čipy prezentace berou tuhle jednu funkci.
 */
export function rizikaProbrana(d) {
  const data = d && typeof d === "object" ? d : {};
  const p = data.rizikaProbrana;
  return !!(p && typeof p === "object" && !Array.isArray(p));
}

/** Staré jméno (do 5. 10. 2026) — táž funkce jako `rizikaProbrana`. */
export const rizikaZodpovezena = rizikaProbrana;

/**
 * Stav fází schůzky podle skutečně vyplněných dat.
 * @returns {{ faze: string, stav: "hotovo"|"aktualni"|"ceka" }[]}
 */
export function stavFazi(d) {
  const data = d && typeof d === "object" ? d : {};
  let aktualniDan = false;
  return FAZE_SCHUZKY.map((faze) => {
    if (FAZE_HOTOVA[faze](data)) return { faze, stav: "hotovo" };
    if (!aktualniDan) { aktualniDan = true; return { faze, stav: "aktualni" }; }
    return { faze, stav: "ceka" };
  });
}
